import { Empty } from "@bednbody/ui/components/empty";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { UsersThreeIcon } from "@phosphor-icons/react";
import type { useAsapQueue } from "../command-center.hooks";

type QueueRows = NonNullable<ReturnType<typeof useAsapQueue>["data"]>;

const TIER_LABEL: Record<string, string> = {
  new_therapist: "New — first duty day",
  rested_yesterday: "Rested yesterday",
  approved_leave: "Approved leave",
  worked_yesterday: "Worked yesterday",
  late_online: "Late online",
  absent_yesterday: "Absent yesterday",
};

/**
 * Today's ASAP fairness queue (Flow 13/14), position 1..n per hub. Read-only
 * by design — every write is a `service_role`-only RPC (0036), so there is no
 * action to take here, only visibility. Mirrors `DispatchQueueCard`'s shape
 * for the rider FIFO; this is the therapist-side, earnings-ranked equivalent.
 */
export function TherapistQueueCard({ queue, isLoading }: { queue: QueueRows; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-12 motion-reduce:animate-none" />
        ))}
      </div>
    );
  }

  if (queue.length === 0) {
    return <Empty>Nobody has clocked in yet today.</Empty>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-xl bg-background px-3 py-2 text-sm">
        <span className="flex items-center gap-2 text-muted-foreground">
          <UsersThreeIcon size={14} className="text-primary" />
          Therapists in today's queue
        </span>
        <span className="font-semibold tabular-nums text-foreground">{queue.length}</span>
      </div>

      <ul className="space-y-2">
        {queue.slice(0, 8).map((entry) => (
          <li
            key={entry.staff_id}
            className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold tabular-nums text-primary">
                {entry.position}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {entry.staff?.full_name ?? "Unknown"}
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {entry.hubs?.name ?? ""}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  {TIER_LABEL[entry.tier] ?? entry.tier}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {queue.length > 8 ? (
        <p className="text-center text-xs text-muted-foreground">
          {queue.length - 8} more in queue
        </p>
      ) : null}
    </div>
  );
}
